import { Pipe, PipeTransform, Inject, LOCALE_ID } from '@angular/core';
import { formatDate } from '@angular/common';
import { AppConfigurationService } from '@app/shared/services/app.configuration.service';
import moment from 'moment-timezone';

/** Eastern US. Used by the explicit `*-est` options. IANA id, not the fixed-offset 'EST'. */
const EASTERN_ZONE = 'America/New_York';

@Pipe({
  name: 'customdateformat',
  standalone: false,
})
export class CustomDateFormatPipe implements PipeTransform {
  constructor(
    @Inject(LOCALE_ID) private locale: string,
    public appConfig: AppConfigurationService
  ) {}
  transform(date: string | Date, option: string = 'date') {
    const FieldNames: any = this.appConfig.getByIdentifier('custom-date-formats');
    const timeZone: any = this.appConfig.getByIdentifier('portal-timezone');
    const zone: string = timeZone?.Fields?.equal?.value;
    if (typeof date === 'string') {
      date = this.getDate(date);
    }
    if (date) {
      const d: Date = date as Date;
      switch (option) {
        case 'date':
          return this.formatInZone(d, FieldNames.Fields.date, zone);
        case 'time':
          return this.formatInZone(d, FieldNames.Fields.time, zone);
        case 'time-est':
          return this.formatInZone(d, FieldNames.Fields.time, EASTERN_ZONE);
        case 'date-est':
          return this.formatInZone(d, FieldNames.Fields.datetime, EASTERN_ZONE);
        case 'datetime':
          return this.formatInZone(d, FieldNames.Fields.datetime, zone);
        case 'day':
          return this.formatInZone(d, FieldNames.Fields.day, zone);
        case 'datemonthyear':
          return this.formatInZone(d, FieldNames.Fields.datemonthyear, zone);
        case 'duedate':
          return this.formatInZone(d, FieldNames.Fields.date, zone);
        case 'admindate':
          // Intentionally renders in the viewer's local timezone (no zone applied).
          if (date && FieldNames && FieldNames.Fields && FieldNames.Fields.date && this.locale) {
            return formatDate(date, FieldNames.Fields.date, this.locale);
          } else {
            return date;
          }
      }
    }
    return date;
  }

  /**
   * Formats an instant as it reads on the wall clock in `zone`.
   *
   * Never pass an IANA zone id straight to formatDate()'s 4th argument.
   * Angular resolves that argument with:
   *
   *     Date.parse('Jan 01, 1970 00:00:00 ' + timezone) / 60000
   *
   * which is NaN for every IANA id ('America/New_York', 'America/Chicago', ...).
   * On NaN it silently falls back to `date.getTimezoneOffset()` — the VIEWER'S
   * browser timezone — so the configured zone was read and then discarded with
   * no error. Midnight-anchored values (e.g. "2026-09-16T04:00:00Z", midnight
   * EDT) landed on the wrong calendar day for any viewer west of the zone.
   *
   * Angular DOES resolve a numeric offset ('-0400'), so we compute the zone's
   * offset at this specific instant with moment-timezone (DST-aware) and hand
   * that over. This also makes the Z/ZZ/O format tokens emit the configured
   * zone's offset instead of the browser's.
   */
  private formatInZone(date: Date, ngFormat: string, zone: string): string {
    if (!zone || typeof zone !== 'string' || !moment.tz.zone(zone)) {
      // No zone configured, or an unknown id: preserve previous behaviour
      // (render in the viewer's local timezone) rather than throwing.
      return formatDate(date, ngFormat, this.locale);
    }
    const offset = moment(date).tz(zone).format('ZZ'); // e.g. '-0400' (EDT) / '-0500' (EST)
    return formatDate(date, ngFormat, this.locale, offset);
  }

  /**
   * Parses an API timestamp into a Date.
   *
   * This is instant-preserving: a Date holds an absolute epoch value and has no
   * timezone of its own, so nothing is shifted here. It does require the string
   * to carry a zone designator (`Z` or an offset) — a bare "2026-09-16T04:00:00"
   * would be read as the browser's local time by Date.parse.
   */
  private getDate(utcDate: string): Date {
    let mtime = Date.parse(utcDate);
    let d = new Date(mtime);
    return d;
  }

  getFormat(option: string = 'date') {
    const FieldNames: any = this.appConfig.getByIdentifier('custom-date-formats');
    switch (option) {
      case 'date':
        return FieldNames.Fields.date;
      case 'time':
        FieldNames.Fields.time;
      case 'datetime':
        return FieldNames.Fields.datetime;
    }
    return FieldNames.Fields.date;
  }
  getFormatForCalendarControlDateFormat(option: string = 'date'): string {
    return (this.getFormat(option) as string).toLowerCase().replace('yyyy', 'yy');
  }
}
